import { useState, useRef, useCallback } from 'react';
import { useApi } from '../../../shared/hooks/useApi';
import { toast } from 'react-toastify';
import type { CccdScanResult, ScanMethod } from '../types';

type ScanPhase = 'idle' | 'scanning' | 'qr-found' | 'ocr-running' | 'done' | 'error';

declare global {
  interface Window {
    BarcodeDetector?: new (opts: { formats: string[] }) => {
      detect: (source: HTMLVideoElement | HTMLCanvasElement | ImageBitmap) => Promise<{ rawValue: string }[]>;
    };
  }
}

type ApiDataQR = {
  success: boolean;
  avatar?: string;
  decodedText?: { data: string; type: string }[];
  DT: unknown[];
  EC: number;
  EM: string;
};

const compressImage = (blob: Blob, maxDim = 1400, quality = 0.82): Promise<Blob> =>
  new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (Math.max(width, height) > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      canvas.toBlob((r) => resolve(r ?? blob), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(blob); };
    img.src = url;
  });

const captureVideoFrame = (video: HTMLVideoElement, quality = 0.85): Promise<Blob | null> =>
  new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });

const safeVal = (v: string | undefined): string =>
  !v || v === 'null' || v === 'null null' || v === 'undefined' ? '' : v;

/** "NGUYỄN VĂN ANH" → "Nguyễn Văn Anh" */
const toTitleCase = (s: string): string =>
  s.toLowerCase().replace(/(^|\s)\S/g, c => c.toUpperCase());

/**
 * Detect if a string looks like a date: 8 digits (DDMMYYYY) or DD/MM/YYYY.
 */
const isDateLike = (s: string): boolean =>
  /^\d{8}$/.test(s.trim()) || /^\d{2}[/.-]\d{2}[/.-]\d{4}$/.test(s.trim());

/**
 * Format raw date (DDMMYYYY or DD/MM/YYYY) → DD/MM/YYYY.
 */
const formatDate = (v: string): string => {
  const s = v.trim();
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 2)}/${s.slice(2, 4)}/${s.slice(4)}`;
  if (/^\d{2}[.-]\d{2}[.-]\d{4}$/.test(s)) return s.replace(/[.-]/g, '/');
  return s;
};

/**
 * Parse pipe-delimited CCCD QR data.
 *
 * Two known formats:
 *   Format A (7 fields — old CCCD):
 *     soCCCD|soCMT|hoTen|ngaySinh|gioiTinh|noiThuongTru|ngayCap
 *
 *   Format B (8+ fields — CCCD gắn chip):
 *     soCCCD|soCMT|hoTen|ngaySinh|gioiTinh|queQuan|noiThuongTru|[dacDiem]|ngayCap
 *
 * Detection: if parts[6] is date-like → Format A, otherwise → Format B.
 */
const parsePipeText = (text: string): CccdScanResult => {
  const parts = text.split('|');

  const soCCCD = safeVal(parts[0]);
  const soCMT = safeVal(parts[1]);
  const hoTen = toTitleCase(safeVal(parts[2]));
  const ngaySinh = safeVal(parts[3]) ? formatDate(safeVal(parts[3])) : '';
  const gioiTinh = safeVal(parts[4]);

  let diaChi = '';
  let ngayCap = '';

  if (parts.length >= 7 && isDateLike(parts[6] ?? '')) {
    // Format A: parts[5] = address, parts[6] = issue/expiry date
    diaChi = safeVal(parts[5]);
    ngayCap = safeVal(parts[6]);
  } else if (parts.length >= 7) {
    // Format B: parts[5] = quê quán, parts[6] = nơi thường trú
    // Use quê quán as primary address
    diaChi = safeVal(parts[5]) || safeVal(parts[6]);
    // Scan backwards for the last date-like field as ngayCap
    for (let i = parts.length - 1; i >= 7; i--) {
      if (isDateLike(parts[i] ?? '')) {
        ngayCap = safeVal(parts[i]);
        break;
      }
    }
  } else {
    diaChi = safeVal(parts[5]);
    ngayCap = safeVal(parts[6]);
  }

  return {
    soCCCD, soCMT, hoTen, ngaySinh, gioiTinh, diaChi, diaChiMoi: '',
    ngayCap: ngayCap ? formatDate(ngayCap) : '',
    avatarBase64: null, sourceImageBase64: null, detectedMethod: null, extraImages: [],
    soGPLX: '', hangGPLX: '', hangGPLXMoi: '', ngayCapGPLX: '', noiCapGPLX: '', donViCapGPLX: '', ngayTrungTuyenGPLX: '', ngayHetHanGPLX: '', maDvhc: '',
  };
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });

export const PHASE_LABELS: Record<ScanPhase, string> = {
  idle: '',
  scanning: 'Đang quét QR...',
  'qr-found': 'Phát hiện QR! Đang trích xuất...',
  'ocr-running': 'Không tìm thấy QR — đang nhận dạng...',
  done: 'Hoàn tất!',
  error: 'Không thể nhận dạng',
};

export const useCccdScanner = () => {
  const { post } = useApi();
  const [result, setResult] = useState<CccdScanResult | null>(null);
  const [phase, setPhase] = useState<ScanPhase>('idle');
  const [scanMethod, setScanMethod] = useState<ScanMethod>('manual');
  const [cameraActive, setCameraActive] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanLoopRef = useRef<number>(0);
  const isBusyRef = useRef(false);

  const reset = useCallback(() => {
    setResult(null);
    setPhase('idle');
    setScanMethod('manual');
  }, []);

  // Run text detection + avatar crop + source image conversion in PARALLEL
  const sendToBackend = useCallback(async (blob: Blob): Promise<CccdScanResult | null> => {
    const compressed = await compressImage(blob);

    // Build 2 identical FormData (can't reuse after fetch consumes it)
    const fdDecode = new window.FormData();
    fdDecode.append('image', compressed, 'scan.jpg');
    const fdCrop = new window.FormData();
    fdCrop.append('image', compressed, 'scan.jpg');

    // Fire all 3 tasks in parallel
    const [decodeResult, cropResult, sourceBase64] = await Promise.all([
      post<{ EC: number; DT: ApiDataQR[] }>('/api/file/qr/decode', fdDecode).catch(() => null),
      post<{ EC: number; DT: string | null }>('/api/file/qr/crop-face', fdCrop).catch(() => null),
      blobToBase64(compressed),
    ]);

    const data = decodeResult?.DT?.[0];
    if (decodeResult?.EC === 0 && data?.success && data.decodedText?.length && data.decodedText[0]?.data) {
      const parsed = parsePipeText(data.decodedText[0].data);
      parsed.detectedMethod = data.decodedText[0].type === 'QR' ? 'qr' : 'ocr';
      parsed.sourceImageBase64 = sourceBase64;

      // Avatar: prefer from decode response (QR path), fallback to crop-face
      const avatar = data.avatar || (cropResult?.EC === 0 ? cropResult.DT : null);
      if (avatar) parsed.avatarBase64 = `data:image/jpeg;base64,${avatar}`;

      return parsed;
    }
    return null;
  }, [post]);

  const stopCamera = useCallback(() => {
    scanLoopRef.current++;
    setCameraActive(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startCamera = useCallback(async () => {
    reset();
    setCameraActive(true);
    setPhase('scanning');

    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase('error');
      toast.error('Trình duyệt không hỗ trợ camera');
      setCameraActive(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        await videoRef.current.play();
      }
    } catch {
      setPhase('error');
      toast.error('Không thể truy cập camera');
      setCameraActive(false);
      return;
    }

    const loopId = ++scanLoopRef.current;
    const hasBarcodeDetector = typeof window.BarcodeDetector !== 'undefined';
    let detector: InstanceType<NonNullable<typeof window.BarcodeDetector>> | null = null;
    if (hasBarcodeDetector) detector = new window.BarcodeDetector!({ formats: ['qr_code'] });

    let frameCount = 0;
    const MAX_FRAMES = hasBarcodeDetector ? 90 : 4;
    const INTERVAL = hasBarcodeDetector ? 33 : 1500;

    const loop = async () => {
      if (loopId !== scanLoopRef.current || !videoRef.current || isBusyRef.current) return;
      frameCount++;

      if (detector && videoRef.current.readyState >= 2) {
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes.length > 0) {
            isBusyRef.current = true;
            setPhase('qr-found');
            const parsed = parsePipeText(codes[0].rawValue);
            parsed.detectedMethod = 'qr';
            setScanMethod('qr');

            const frame = await captureVideoFrame(videoRef.current);
            if (frame) {
              const compressed = await compressImage(frame);

              // Show text info immediately (no avatar yet)
              parsed.sourceImageBase64 = await blobToBase64(compressed);
              setResult({ ...parsed });
              setPhase('done');
              toast.success('Quét QR thành công!');
              stopCamera();

              // Fetch avatar in background — updates when ready
              const fd = new window.FormData();
              fd.append('image', compressed, 'scan.jpg');
              post<{ EC: number; DT: string | null }>('/api/file/qr/crop-face', fd)
                .then((resp) => {
                  if (resp?.EC === 0 && resp.DT) {
                    setResult((prev) => prev ? { ...prev, avatarBase64: `data:image/jpeg;base64,${resp.DT}` } : prev);
                  }
                })
                .catch(() => { /* non-critical */ });

              // If address empty → call backend OCR to detect quê quán from card text
              if (!parsed.diaChi) {
                const fdOcr = new window.FormData();
                fdOcr.append('image', compressed, 'scan.jpg');
                post<{ EC: number; DT: ApiDataQR[] }>('/api/file/qr/decode', fdOcr)
                  .then((resp) => {
                    const ocrApiData = resp?.DT?.[0];
                    if (ocrApiData?.success && ocrApiData.decodedText?.[0]?.data) {
                      const ocrParsed = parsePipeText(ocrApiData.decodedText[0].data);
                      if (ocrParsed.diaChi) {
                        setResult((prev) => prev ? { ...prev, diaChi: ocrParsed.diaChi } : prev);
                      }
                    }
                  })
                  .catch(() => { /* non-critical */ });
              }
            } else {
              setResult(parsed);
              setPhase('done');
              toast.success('Quét QR thành công!');
              stopCamera();
            }

            isBusyRef.current = false;
            return;
          }
        } catch { /* continue scanning */ }
      }

      if (!hasBarcodeDetector && videoRef.current.readyState >= 2) {
        isBusyRef.current = true;
        try {
          const frame = await captureVideoFrame(videoRef.current);
          if (frame) {
            const res = await sendToBackend(frame);
            if (res) {
              setScanMethod(res.detectedMethod || 'qr');
              setResult(res);
              setPhase('done');
              toast.success(res.detectedMethod === 'ocr' ? 'Nhận dạng thành công!' : 'Quét QR thành công!');
              stopCamera();
              isBusyRef.current = false;
              return;
            }
          }
        } catch { /* continue */ }
        isBusyRef.current = false;
      }

      if (frameCount >= MAX_FRAMES) {
        isBusyRef.current = true;
        setPhase('ocr-running');
        try {
          const frame = await captureVideoFrame(videoRef.current);
          if (frame) {
            const res = await sendToBackend(frame);
            if (res) {
              setScanMethod(res.detectedMethod || 'ocr');
              setResult(res);
              setPhase('done');
              toast.success(res.detectedMethod === 'qr' ? 'Quét QR thành công!' : 'Nhận dạng thành công!');
            } else {
              setPhase('error');
              toast.error('Không thể nhận dạng. Thử tải ảnh lên.');
            }
          }
        } catch {
          setPhase('error');
        }
        stopCamera();
        isBusyRef.current = false;
        return;
      }

      if (loopId === scanLoopRef.current) setTimeout(loop, INTERVAL);
    };

    setTimeout(loop, 500);
  }, [reset, sendToBackend, stopCamera, post]);

  const scanFromFile = useCallback(async (file: File) => {
    reset();
    setPhase('scanning');

    const blob = new Blob([file], { type: file.type });
    try {
      // One-shot call to /api/file/qr/decode. Backend runs pyzbar QR +
      // Mistral OCR IN PARALLEL, then returns the QR result if QR succeeds
      // (priority), else falls back to OCR. Avatar is extracted alongside.
      // No client-side pre-probe needed — keeps the code path clean + avoids
      // re-racing Python's face detector.
      const res = await sendToBackend(blob);
      if (res) {
        setScanMethod(res.detectedMethod || 'ocr');
        setResult(res);
        setPhase('done');
        toast.success(res.detectedMethod === 'qr'
          ? 'Quét QR thành công — chính xác 100%'
          : 'Nhận dạng bằng OCR — đối chiếu kỹ thông tin');
      } else {
        setPhase('error');
        toast.error('Không thể nhận dạng thông tin từ ảnh');
      }
    } catch {
      setPhase('error');
      toast.error('Lỗi khi xử lý ảnh');
    }
  }, [reset, sendToBackend]);

  return {
    result,
    phase,
    scanMethod,
    cameraActive,
    videoRef,
    reset,
    startCamera,
    stopCamera,
    scanFromFile,
    setResult,
    setScanMethod,
  };
};
